import React, { useEffect, useState } from "react";
import InputForm from "./InputForm";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../utils/config";
import { useAuth } from "../../contexts/AuthProvider";
const baseUrl = API_URL;

const AddSupplier = () => {
  const { t } = useTranslation("supplier");
  const [Errors, setErrors] = useState({});
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    service_type: "",
    service_types: [],
    country: "",
    city: "",
    cities: [],
    supported_email: "",
    shipping_prices: {},
    website: "",
    about: "",
    terms_and_conditions: "",
    preview: null,
    portfolio_url: "",
    image: null,
  });

  // Image focus state for supplier image
  const [imageFocus, setImageFocus] = useState({});

  const { isAuthenticated } = useAuth();
  // Real-time validation function
  const validateField = (name, value) => {
    const newErrors = { ...Errors };

    switch (name) {
      case "name":
        if (!value || value.trim().length < 3) {
          newErrors.name = t("validation.nameRequired");
        } else {
          delete newErrors.name;
        }
        break;

      case "service_type":
        if (!value) {
          newErrors.service_type = t("validation.serviceTypeRequired");
        } else {
          delete newErrors.service_type;
        }
        break;

      case "country":
        if (!value) {
          newErrors.country = t("validation.countryRequired");
        } else {
          delete newErrors.country;
        }
        break;

      case "city":
        if (!value) {
          newErrors.city = t("validation.cityRequired");
        } else {
          delete newErrors.city;
        }
        break;

      case "supported_email":
        if (!value) {
          newErrors.supported_email = t("validation.emailRequired");
        } else if (
          !/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(value)
        ) {
          newErrors.supported_email = t("validation.emailInvalid");
        } else {
          delete newErrors.supported_email;
        }
        break;

      case "shipping_prices": {
        const cityIds = Array.isArray(formData.cities)
          ? formData.cities
          : formData.city
            ? [formData.city]
            : [];
        const prices = value && typeof value === "object" ? value : {};
        const missingOrInvalid = cityIds.some((cityId) => {
          const price = prices[cityId] ?? prices[String(cityId)];
          return (
            price === "" ||
            price == null ||
            isNaN(Number(price)) ||
            Number(price) < 0
          );
        });
        if (cityIds.length === 0 || missingOrInvalid) {
          newErrors.shipping_prices = t("validation.shippingPricesRequired");
        } else {
          delete newErrors.shipping_prices;
        }
        break;
      }

      case "website":
        if (
          value &&
          !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/.test(
            value
          )
        ) {
          newErrors.website = t("validation.websiteInvalid");
        } else {
          delete newErrors.website;
        }
        break;

      case "about":
        if (!value || value.trim().length < 10) {
          newErrors.about = t("validation.aboutRequired");
        } else {
          delete newErrors.about;
        }
        break;

      case "terms_and_conditions":
        if (!value || value.trim().length < 10) {
          newErrors.terms_and_conditions = t("validation.termsRequired");
        } else {
          delete newErrors.terms_and_conditions;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  // Enhanced setFormData with validation
  const handleFormDataChange = (newFormData) => {
    setFormData(newFormData);

    // Validate the changed field
    const changedField = Object.keys(newFormData).find(
      (key) => newFormData[key] !== formData[key]
    );

    if (changedField) {
      validateField(changedField, newFormData[changedField]);
    }
  };

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  useEffect(() => {
    const fetchSupplier = async () => {
      if (!isAuthenticated) {
        setError(new Error(t("alerts.noAuth")));
        setLoading(false);
        navigate("/login");
        return;
      }

      try {
        const supplierResponse = await fetch(
          `${baseUrl}/api/v1/attnd-supplier/suppliers/${id}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              lang: getLangFromLocalStorage(),
            },
          }
        );

        const Data = await supplierResponse.json();

        if (!supplierResponse.ok) {
          if (supplierResponse.status === 401) {
            navigate("/login");
            throw new Error(t("alerts.noAuth"));
          } else if (supplierResponse.status === 404) {
            alert(t("alerts.supplierDeleted"));
          } else {
            throw new Error(
              `An error occurred: ${Data.error || supplierResponse.statusText}`
            );
          }
        }
        const supplier = Data.data;
        console.log("supplier", supplier);
        const cityIds = Array.isArray(supplier.cities)
          ? supplier.cities
          : supplier.city
            ? [supplier.city]
            : [];
        const shippingPricesMap = {};
        if (Array.isArray(supplier.shipping_prices) && supplier.shipping_prices.length > 0) {
          supplier.shipping_prices.forEach((entry) => {
            if (entry?.city_id == null) return;
            shippingPricesMap[entry.city_id] = String(entry.price ?? "");
          });
        } else {
          const legacyPrice =
            supplier.shipping_price != null && supplier.shipping_price !== ""
              ? String(supplier.shipping_price)
              : "";
          cityIds.forEach((cityId) => {
            shippingPricesMap[cityId] = legacyPrice;
          });
        }
        setFormData({
          name: supplier.name || "",
          service_type: supplier.service_type || "",
          service_types: Array.isArray(supplier.service_types)
            ? supplier.service_types
            : supplier.service_type
              ? [supplier.service_type]
              : [],
          country: supplier.country || "",
          city: supplier.city || "",
          cities: cityIds,
          supported_email: supplier.supported_email || "",
          website: supplier.website || "",
          about: supplier.about || "",
          terms_and_conditions: supplier.terms_and_conditions || "",
          preview: supplier.image,
          shipping_prices: shippingPricesMap,
          portfolio_url: supplier.portfolio_url || "",
          image: null,
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchSupplier();
    } else setLoading(false);
  }, [baseUrl, id, navigate, t]);

  function objectToFormData(obj) {
    const formPayload = new FormData();
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

      if (key === "shipping_prices") {
        const cityIds = Array.isArray(obj.cities) ? obj.cities : [];
        const shippingPricesPayload = cityIds.map((cityId) => ({
          city_id: cityId,
          price: Number(obj.shipping_prices?.[cityId] ?? obj.shipping_prices?.[String(cityId)]),
        }));
        formPayload.append("shipping_prices", JSON.stringify(shippingPricesPayload));
        if (shippingPricesPayload.length > 0) {
          formPayload.append("shipping_price", String(shippingPricesPayload[0].price));
        }
        continue;
      }

      if (obj[key] == null) continue;

      if (Array.isArray(obj[key])) {
        formPayload.append(key, obj[key].join(","));
        continue;
      }

      formPayload.append(key, obj[key]);
    }
    return formPayload;
  }

  const Add = async () => {
    if (adding) return;
    setAdding(true);
    const loadingToast = toast.loading(t("toast.loading"));
    try {
      if (!isAuthenticated) throw new Error(t("alerts.noAuth"));

      const Data = objectToFormData(formData);

      const url = id
        ? `${baseUrl}/api/v1/attnd-supplier/suppliers/${id}`
        : `${baseUrl}/api/v1/attnd-supplier/suppliers/create`;

      const method = id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          lang: getLangFromLocalStorage(),
        },
        body: Data,
      });

      if (!response.ok) {
        if (response.status === 400) {
          let badRequestMessage = "Bad request occurred.";
          try {
            const errorData = await response.json();

            // Handle API errors and update local errors state
            if (errorData.errors) {
              if (Array.isArray(errorData.errors)) {
                // Handle array format
                const apiErrors = {};
                errorData.errors.forEach((errorObj) => {
                  Object.entries(errorObj).forEach(([field, message]) => {
                    apiErrors[field] = message;
                  });
                });
                setErrors(apiErrors);
              } else {
                // Handle object format
                setErrors(errorData.errors);
              }
            }

            badRequestMessage =
              errorData.message || errorData.error || badRequestMessage;
          } catch {
            // ignore JSON parse errors
          }
          toast.update(loadingToast, {
            render: badRequestMessage,
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
          setAdding(false);
          return;
        }

        if (response.status === 401) {
          navigate("/login");
          throw new Error(t("alerts.noAuth"));
        }

        const error = await response.json();
        throw new Error(
          error.message || error.error || "An unknown error occurred."
        );
      }

      await response.json();
      toast.update(loadingToast, {
        render: t("confirmModal.successMessage", {
          action: id
            ? t("confirmModal.buttons.edited")
            : t("confirmModal.buttons.added"),
        }),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      setLoading(false);
      setConfirm(true);
    } catch (error) {
      console.error(error);
      toast.update(loadingToast, {
        render: t("toast.error", { error: error.message }),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      setLoading(false);
    } finally {
      setAdding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await Add();
  };

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        {t("alerts.fetchError", { error: error.message })}
      </div>
    );
  }

  return (
    <div className={"flex flex-col pt-[48px] px-[24px]"}>
      <h3
        className={
          "w-[200px] h-[32px] font-inter font-black text-2xl leading-8 text-[#292929]"
        }
      >
        {t("page.title")}
      </h3>
      <p
        className={
          "mt-[8px] h-[22px] font-inter font-light text-base leading-[22px] text-[#464646]"
        }
      >
        {t("page.subtitle")}
      </p>
      <InputForm
        error={{ errors: Errors }}
        formData={formData}
        setFormData={handleFormDataChange}
        handleSubmit={handleSubmit}
        imageFocus={imageFocus}
        setImageFocus={setImageFocus}
      />
      <ToastContainer />
      {confirm && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-50">
          <div className="p-6 gap-[31px] box-border flex flex-col items-center fixed w-[400px] h-[286px] left-[calc(50%-196px)] top-[calc(50%-153px)] bg-white shadow-lg backdrop-filter backdrop-blur-[25px] rounded-t-lg">
            <svg
              className={"mt-"}
              width="104"
              height="104"
              viewBox="0 0 104 104"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g>
                <path
                  d="M52 104C80.7188 104 104 80.7188 104 52C104 23.2812 80.7188 0 52 0C23.2812 0 0 23.2812 0 52C0 80.7188 23.2812 104 52 104Z"
                  fill="#77CC00"
                />
                <path
                  d="M103.443 59.6375C100.573 79.1375 86.8806 95.1113 68.6664 101.278L22.2686 54.8722L32.3232 44.8175L43.5256 56.0219L71.6767 27.8708L103.443 59.6375Z"
                  fill="url(#paint0_linear_3878_14599)"
                />
                <path
                  d="M81.7314 37.9234L53.5803 66.0766L52 67.6569L43.5256 76.1292L33.4709 66.0766L22.2686 54.8722L32.3232 44.8175L43.5256 56.0219L52 47.5475L71.6767 27.8708L81.7314 37.9234Z"
                  fill="#FFF5F5"
                />
              </g>
              <defs>
                <linearGradient
                  id="paint0_linear_3878_14599"
                  x1="41.3704"
                  y1="35.7703"
                  x2="91.5687"
                  y2="85.9686"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#288900" />
                  <stop offset="1" stop-color="#288900" stop-opacity="0" />
                </linearGradient>
                <clipPath id="clip0_3878_14599">
                  <rect width="104" height="104" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <p
              className={
                "h-[24px] font-inter font-bold text-[18px] leading-[24px] text-center text-[#292929]"
              }
            >
              {t("confirmModal.successMessage", {
                action: id
                  ? t("confirmModal.buttons.edited")
                  : t("confirmModal.buttons.added"),
              })}
            </p>
            <div
              className={
                "flex flex-row gap-2 justify-center items-start p-0 h-[48px]"
              }
            >
              <button
                className={
                  "font-inter font-medium text-[16px] leading-[18px] text-[#5507CF]  box-border w-[172px] h-[48px] bg-[#F4F1FF] border border-[#DBDBDB] shadow-sm rounded-lg"
                }
                onClick={() => navigate(-1)}
              >
                {t("confirmModal.buttons.cancel")}
              </button>
              <button
                className={
                  "font-inter font-semibold text-[16px] leading-[18px] text-white h-[48px] w-[172px] bg-[#6609F8] shadow-sm rounded-lg"
                }
                onClick={() => {
                  if (id) {
                    navigate(`/suppliers/${id}`);
                  } else {
                    navigate("/products/add-new-product", { replace: true });
                  }
                }}
              >
                {id
                  ? t("confirmModal.buttons.ok")
                  : t("confirmModal.buttons.addProduct")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSupplier;
